/**
 * Currency Input Component
 * Specialized input for BRL currency values with formatting
 */

import React, { useState, useEffect } from 'react';

interface CurrencyInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  helpText?: string;
}

export function CurrencyInput({
  label,
  value,
  onChange,
  placeholder = '0,00',
  required = false,
  className = '',
  helpText,
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (value === 0) {
      setDisplayValue('');
    } else {
      setDisplayValue(formatToBRL(value));
    }
  }, [value]);

  const formatToBRL = (num: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const parseFromBRL = (str: string): number => {
    const numbers = str.replace(/[^\d]/g, '');
    return numbers ? parseFloat(numbers) / 100 : 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const numericValue = parseFromBRL(rawValue);

    onChange(numericValue);
  };

  const handleBlur = () => {
    if (value > 0) {
      setDisplayValue(formatToBRL(value));
    }
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="block text-sm font-medium text-zinc-200">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-medium">
          R$
        </span>
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="w-full pl-12 pr-4 py-3 bg-zinc-900/50 border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#B05D41]/50 focus:border-[#B05D41]/50 transition-all"
        />
      </div>
      {helpText && (
        <p className="text-xs text-zinc-400">{helpText}</p>
      )}
    </div>
  );
}
